import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut, Building2, User, ShieldCheck, MessageCircle, Wallet, Plus, Trash2, Receipt, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import { useAuth } from "@/context/AuthContext";

const WHATSAPP = "918839790836";

export default function SettingsView() {
  const { user, businessName, venues, businessId, refetchVenues } = useDash();
  const { isAdmin, logout, business } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isOwner = user?.role === "owner";
  const [empAccess, setEmpAccess] = useState(!!business?.employee_expense_access);
  const [savingAccess, setSavingAccess] = useState(false);
  const [newVenue, setNewVenue] = useState("");
  const [addingVenue, setAddingVenue] = useState(false);
  const [deletingVenue, setDeletingVenue] = useState(null);
  const [tmpl, setTmpl] = useState("");
  const [tmplLoaded, setTmplLoaded] = useState(false);
  const [savingTmpl, setSavingTmpl] = useState(false);

  useEffect(() => { setEmpAccess(!!business?.employee_expense_access); }, [business?.employee_expense_access]);

  useEffect(() => {
    if (!(isOwner || isAdmin) || !businessId) return;
    api.get("/settings/confirmation-template", { params: businessId ? { business_id: businessId } : {} })
      .then((r) => { setTmpl(r.data.template || ""); setTmplLoaded(true); })
      .catch(() => setTmplLoaded(true));
  }, [isOwner, isAdmin, businessId]);

  const saveTmpl = async () => {
    setSavingTmpl(true);
    try {
      await api.put("/settings/confirmation-template", { template: tmpl }, { params: businessId ? { business_id: businessId } : {} });
      toast.success("Confirmation message template saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    } finally { setSavingTmpl(false); }
  };

  const toggleAccess = async () => {
    const next = !empAccess;
    setSavingAccess(true);
    try {
      await api.post("/settings/employee-expense-access", { enabled: next }, { params: businessId ? { business_id: businessId } : {} });
      setEmpAccess(next);
      toast.success(next ? "Employees can now manage Profit & Loss" : "Employee access to Profit & Loss removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setSavingAccess(false); }
  };

  const addVenue = async (e) => {
    e.preventDefault();
    if (!newVenue.trim()) return;
    setAddingVenue(true);
    try {
      await api.post("/venues", { name: newVenue.trim() }, { params: businessId ? { business_id: businessId } : {} });
      toast.success(`"${newVenue.trim()}" added`);
      setNewVenue("");
      refetchVenues?.();
      qc.invalidateQueries({ queryKey: ["venues"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add venue");
    } finally { setAddingVenue(false); }
  };

  const deleteVenue = async (v) => {
    if (!window.confirm(`Delete "${v.name}"? This can't be undone.`)) return;
    setDeletingVenue(v.venue_id);
    try {
      await api.delete(`/venues/${v.venue_id}`, { params: businessId ? { business_id: businessId } : {} });
      toast.success(`"${v.name}" deleted`);
      refetchVenues?.();
      qc.invalidateQueries({ queryKey: ["venues"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete venue");
    } finally { setDeletingVenue(null); }
  };

  const extra = Math.max(0, venues.length - 1);

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-pulse-navy">Settings</h1>
        <p className="text-sm text-slate">Your account and business details.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-pulse-sm">
        <div className="flex items-center gap-2 text-sm font-semibold text-pulse-navy"><User size={18} className="text-pulse-green" /> Account</div>
        <div className="mt-4 flex items-center gap-4">
          {user?.picture ? <img src={user.picture} className="h-14 w-14 rounded-full object-cover" alt="" /> : <div className="grid h-14 w-14 place-items-center rounded-full bg-pulse-navy text-white">{user?.name?.[0]}</div>}
          <div>
            <div className="font-semibold text-charcoal">{user?.name}</div>
            <div className="text-sm text-slate">{user?.email}</div>
            <span className="mt-1 inline-block rounded bg-cloud px-2 py-0.5 text-xs font-medium capitalize text-slate">{user?.role}</span>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-pulse-sm">
        <div className="flex items-center gap-2 text-sm font-semibold text-pulse-navy"><Building2 size={18} className="text-pulse-green" /> Business</div>
        <div className="mt-4">
          <div className="font-semibold text-charcoal">{businessName}</div>
          <div className="mt-3 space-y-2">
            {venues.map((v) => (
              <div key={v.venue_id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2" data-testid={`venue-row-${v.venue_id}`}>
                <span className="flex items-center gap-2 text-sm font-medium">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />
                  <span className="text-charcoal">{v.name}</span>
                </span>
                {(isOwner || isAdmin) && venues.length > 1 && (
                  <button onClick={() => deleteVenue(v)} disabled={deletingVenue === v.venue_id} data-testid={`delete-venue-${v.venue_id}`}
                    className="grid h-8 w-8 place-items-center rounded-md text-slate transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {(isOwner || isAdmin) && (
            <div className="mt-4 rounded-lg border border-slate-200 p-4" data-testid="add-venue-card">
              <div className="text-sm font-medium text-pulse-navy">Add a venue</div>
              <form onSubmit={addVenue} className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input value={newVenue} onChange={(e) => setNewVenue(e.target.value)} data-testid="add-venue-input"
                  placeholder="New venue name" className="flex-1 rounded-md border border-slate-200 px-4 py-2.5 text-base outline-none focus:border-pulse-green" />
                <button type="submit" disabled={addingVenue} data-testid="add-venue-btn"
                  className="flex items-center justify-center gap-2 rounded-md bg-pulse-navy px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.97] disabled:opacity-60">
                  <Plus size={16} /> {addingVenue ? "Adding…" : "Add venue"}
                </button>
              </form>
              <p className="mt-2 text-xs text-slate" data-testid="add-venue-note">
                Add as many venues as you like — <b className="text-pulse-navy">free during your trial</b>. Each extra venue costs <b className="text-pulse-navy">₹2,000/year</b> (or ₹200/month) after you subscribe.
              </p>
            </div>
          )}
        </div>
      </div>

      {(isOwner || isAdmin) && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-pulse-sm" data-testid="billing-preview-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-pulse-navy"><Receipt size={18} className="text-pulse-green" /> Your plan after the trial</div>
          <p className="mt-1 text-sm text-slate">Based on your <b className="text-charcoal">{venues.length}</b> venue{venues.length > 1 ? "s" : ""} (1 included{extra > 0 ? ` + ${extra} extra` : ""}).</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-slate">Yearly</div>
              <div className="mt-1 text-2xl font-bold text-pulse-navy" data-testid="billing-yearly">₹{(10080 + extra * 2000).toLocaleString("en-IN")}<span className="text-sm font-medium text-slate">/year</span></div>
              {extra > 0 && <div className="mt-1 text-xs text-slate">₹10,080 + {extra}×₹2,000</div>}
            </div>
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-slate">Monthly</div>
              <div className="mt-1 text-2xl font-bold text-pulse-navy" data-testid="billing-monthly">₹{(1000 + extra * 200).toLocaleString("en-IN")}<span className="text-sm font-medium text-slate">/month</span></div>
              {extra > 0 && <div className="mt-1 text-xs text-slate">₹1,000 + {extra}×₹200</div>}
            </div>
          </div>
          <p className="mt-3 text-xs text-slate">You won't be charged during your 15-day trial — payments open up when checkout goes live.</p>
        </div>
      )}

      {isOwner && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-pulse-sm" data-testid="perm-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-pulse-navy"><Wallet size={18} className="text-pulse-green" /> Team &amp; permissions</div>
          <div className="mt-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-medium text-charcoal">Let employees manage Profit &amp; Loss</div>
              <div className="mt-1 text-sm text-slate">By default only you can record expenses and see profit. Turn this on to let your employees add expenses in the Calendar's Profit &amp; Loss section.</div>
            </div>
            <button onClick={toggleAccess} disabled={savingAccess} data-testid="toggle-emp-expense"
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${empAccess ? "bg-pulse-green" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${empAccess ? "translate-x-[22px]" : "translate-x-0.5"}`} />
            </button>
          </div>
        </div>
      )}


      {(isOwner || isAdmin) && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-pulse-sm" data-testid="confirmation-template-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-pulse-navy"><MessageSquare size={18} className="text-pulse-green" /> Confirmation message</div>
          <p className="mt-1 text-sm text-slate">The message you send clients after a booking. It auto-fills the details below — edit the wording to match how you send it.</p>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="template-placeholders">
            {["{greeting}", "{venue}", "{guest_name}", "{contact}", "{checkin_date}", "{checkout_date}", "{amount}"].map((p) => (
              <span key={p} className="rounded bg-cloud px-2 py-1 font-mono text-xs text-pulse-navy">{p}</span>
            ))}
          </div>
          <textarea data-testid="template-textarea" value={tmpl} onChange={(e) => setTmpl(e.target.value)} rows={12}
            disabled={!tmplLoaded}
            className="mt-3 w-full resize-y rounded-md border border-slate-200 p-3 text-sm leading-relaxed text-charcoal outline-none focus:border-pulse-green disabled:opacity-60" />
          <button onClick={saveTmpl} disabled={savingTmpl || !tmplLoaded} data-testid="save-template-btn"
            className="mt-3 rounded-md bg-pulse-navy px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.97] disabled:opacity-60">
            {savingTmpl ? "Saving…" : "Save template"}
          </button>
        </div>
      )}

      {isAdmin && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6" data-testid="admin-settings-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-800"><ShieldCheck size={18} /> Admin</div>
          <p className="mt-2 text-sm text-amber-700">Manage all businesses, provision clients and view the audit log.</p>
          <button onClick={() => navigate("/admin")} data-testid="open-admin-btn"
            className="mt-4 rounded-md bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.97]">Open Admin Panel</button>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-pulse-navy transition-colors hover:bg-cloud">
          <MessageCircle size={16} /> Contact support
        </a>
        <button onClick={logout} data-testid="settings-logout-btn"
          className="flex items-center justify-center gap-2 rounded-md border border-red-200 px-5 py-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </div>
  );
}
